import { z } from 'zod';
import { PROTOCOL_VERSION } from './protocol.js';
import { PublicUserSchema } from './auth.js';
import { ActiveContractSchema, ContractOfferSchema } from './sim/contracts.js';
import { BidBoardSnapshotSchema } from './sim/bidBoard.js';
import { MarketStateSchema } from './sim/market.js';
import { PowerBudgetSchema } from './sim/power.js';
import { ALL_RESOURCE_IDS } from './sim/resources.js';
import { EmblemSchema } from './sim/emblem.js';
import { MACHINE_SPECS, RECIPES } from './sim/machines.js';
import { ALL_DRONE_CLASSES } from './sim/drones.js';
/**
 * Structural Zod guard for a Station. Runtime check is intentionally loose —
 * server is the sole source of these payloads, and enumerating every nested
 * shape would double-maintain with `entities.ts`. The static type is tight.
 */
const StationSchema = z.custom((val) => typeof val === 'object' &&
    val !== null &&
    'id' in val &&
    'machines' in val &&
    'drones' in val, { message: 'invalid Station payload' });
const PlayerSchema = z.custom((val) => typeof val === 'object' &&
    val !== null &&
    'credits' in val &&
    'reputation' in val, { message: 'invalid Player payload' });
const TuningOverrideSchema = z.custom((val) => typeof val === 'object' &&
    val !== null &&
    typeof val.key === 'string' &&
    typeof val.value === 'number' &&
    typeof val.setAt === 'number', { message: 'invalid TuningOverride payload' });
/** P8 — reactive event payload. Loose check (server is sole producer). */
const ActiveEventSchema = z.custom((val) => typeof val === 'object' &&
    val !== null &&
    typeof val.id === 'string' &&
    typeof val.kind === 'string' &&
    typeof val.endsAtTick === 'number', { message: 'invalid ActiveEvent payload' });
/** P11 — Resonant Matrix bottleneck report. Same loose pattern as events. */
const BottleneckReportSchema = z.custom((val) => typeof val === 'object' &&
    val !== null &&
    typeof val.generatedAtTick === 'number' &&
    typeof val.unlocked === 'boolean' &&
    Array.isArray(val.entries), { message: 'invalid BottleneckReport payload' });
/**
 * All wire messages live here as Zod schemas. Both client and server import
 * these, so runtime validation and TypeScript types share a single source of
 * truth. Add new message types by:
 *
 *   1. define a Zod schema with `type: z.literal('foo')`
 *   2. add it to `ClientMessageSchema` or `ServerMessageSchema`
 *   3. the inferred union type updates automatically
 */
// ---------- Client → Server ----------
export const HelloMessage = z.object({
    type: z.literal('hello'),
    protocol: z.literal(PROTOCOL_VERSION),
    /** Optional — if the client has a stored session from a prior run, resume. */
    resumeSessionId: z.string().uuid().optional(),
});
export const PingMessage = z.object({
    type: z.literal('ping'),
    /** Opaque client-generated id echoed back in the corresponding pong. */
    nonce: z.string().min(1),
    /** Client timestamp (epoch ms) when sent. */
    clientTs: z.number().int().nonnegative(),
});
// ---------- build commands (client → server) ----------
const PositionSchema = z.object({
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
});
// Wire enums derived directly from the sim catalogs — no double-maintenance.
// Adding a ResourceId / MachineSpecId / RecipeId in `./sim/` automatically
// makes it valid on the wire.
//
// `z.enum` requires a non-empty literal tuple, so we cast the runtime arrays
// to the narrow literal-tuple types. The catalog can never be empty (the
// server wouldn't boot), so the cast is safe; the type system just can't see
// that statically. Importing the narrow union types preserves them at the
// inferred-output level so downstream messages still get `ResourceId`, etc.
const ResourceIdSchema = z.enum(ALL_RESOURCE_IDS);
const MachineSpecIdSchema = z.enum(Object.keys(MACHINE_SPECS));
const RecipeIdSchema = z.enum(Object.keys(RECIPES));
const DroneClassSchema = z.enum(ALL_DRONE_CLASSES);
const DroneRuleSchema = z.object({
    sourceId: z.string().min(1),
    destinationId: z.string().min(1),
    resource: ResourceIdSchema,
    loadThreshold: z.number().int().positive(),
    capacity: z.number().int().positive(),
});
export const PlaceMachineMessage = z.object({
    type: z.literal('placeMachine'),
    requestId: z.string().min(1),
    specId: MachineSpecIdSchema,
    /** `null` for storage silos; otherwise must be a recipe the spec supports. */
    recipeId: RecipeIdSchema.nullable(),
    position: PositionSchema,
});
export const RemoveMachineMessage = z.object({
    type: z.literal('removeMachine'),
    requestId: z.string().min(1),
    machineId: z.string().min(1),
});
export const PlaceDroneMessage = z.object({
    type: z.literal('placeDrone'),
    requestId: z.string().min(1),
    position: PositionSchema,
    rule: DroneRuleSchema,
    /**
     * P6: drone class. Optional for backwards-compat with older clients that
     * don't yet include the picker — the server falls back to `'freighter'`,
     * the legacy default. Validated against `class.cargoPhases` server-side.
     */
    droneClass: DroneClassSchema.optional(),
});
export const RemoveDroneMessage = z.object({
    type: z.literal('removeDrone'),
    requestId: z.string().min(1),
    droneId: z.string().min(1),
});
export const ConfigureDroneRuleMessage = z.object({
    type: z.literal('configureDroneRule'),
    requestId: z.string().min(1),
    droneId: z.string().min(1),
    rule: DroneRuleSchema,
});
/**
 * P5 — switch a machine's active recipe (e.g. flip a Smelter between
 * `smelt-iron-ingot` and `refine-fuel`). The new recipe must be in the
 * machine spec's `recipes` array; otherwise the server returns
 * INCOMPATIBLE_RECIPE. The input/output buffers stay as-is — partial
 * progress on the previous cycle is dropped so a half-done smelt doesn't
 * pop out a fuel can.
 */
export const SetMachineRecipeMessage = z.object({
    type: z.literal('setMachineRecipe'),
    requestId: z.string().min(1),
    machineId: z.string().min(1),
    recipeId: RecipeIdSchema,
});
// ---------- admin: tuning (client → server) ----------
/**
 * Set an admin tuning override. Value is validated server-side against the
 * range table in `sim/tuning.ts`. `adminToken` is checked against the
 * server's `ADMIN_TOKEN` env var; in dev with no token set, any string is
 * accepted (the server logs a warning on boot).
 */
export const SetTuningMessage = z.object({
    type: z.literal('setTuning'),
    requestId: z.string().min(1),
    adminToken: z.string(),
    key: z.string().min(1),
    value: z.number(),
});
/** Remove a previously-set tuning override, reverting to the baseline. */
export const ClearTuningMessage = z.object({
    type: z.literal('clearTuning'),
    requestId: z.string().min(1),
    adminToken: z.string(),
    key: z.string().min(1),
});
/**
 * P4 contract actions. The player picks an offer from the board or walks
 * away from an active contract; both are acked via CommandResultMessage.
 */
export const AcceptContractMessage = z.object({
    type: z.literal('acceptContract'),
    requestId: z.string().min(1),
    offerId: z.string().min(1),
});
export const AbandonContractMessage = z.object({
    type: z.literal('abandonContract'),
    requestId: z.string().min(1),
    contractId: z.string().min(1),
});
/**
 * Recovery — wipe every currently-active contract on the player's
 * active world without the per-contract rep penalty. Targets the
 * post-bug scenario where contracts ended up with stale `deadlineTick`
 * values from an earlier world-tick lineage and now render 0:00 on the
 * client. Idempotent (no-op when there are no active contracts).
 */
export const ResetActiveContractsMessage = z.object({
    type: z.literal('resetActiveContracts'),
    requestId: z.string().min(1),
});
/**
 * P22 — accept an offer from the **shared** Bid Board (separate from the
 * personal contract pool). Race-resistant: the server's BidBoardRegistry
 * holds the canonical pool; first valid `acceptSharedContract` for an
 * offer wins, all subsequent attempts get `OFFER_ALREADY_TAKEN`. Winner
 * gets the offer added to their personal `activeContracts` at the
 * premium payout/duration.
 */
export const AcceptSharedContractMessage = z.object({
    type: z.literal('acceptSharedContract'),
    requestId: z.string().min(1),
    offerId: z.string().min(1),
});
/**
 * P22 v3 — Place / raise a sealed bid on an auction-mode bid-board offer.
 *
 *   - Server validates: offer exists, mode === 'auction', not yet settled,
 *     `bid` ≥ `offer.minBid`, `bid` is a strict raise over the player's
 *     own previous bid (if any) by at least `AUCTION_BID_INCREMENT`,
 *     player has enough free credits.
 *   - On success: credits are debited by `(bid - previousBid)` (i.e. the
 *     delta — the player's running escrow grows to `bid`). The bid is
 *     stored privately on the registry; other players don't see it.
 *   - At the offer's `offerExpiresTick`, the highest bid wins; that
 *     charter gets the offer as an active contract; losers are refunded;
 *     the winning bid is destroyed (credit sink). Settlement results
 *     land in the snapshot's `bidBoard.recentAuctionSettlements`.
 *
 * Errors: `OFFER_NOT_FOUND`, `NOT_AN_AUCTION`, `AUCTION_CLOSED`,
 * `BID_BELOW_MIN`, `BID_TOO_SMALL_RAISE`, `INSUFFICIENT_CREDITS`.
 */
export const BidSharedContractMessage = z.object({
    type: z.literal('bidSharedContract'),
    requestId: z.string().min(1),
    offerId: z.string().min(1),
    bid: z.number().int().positive(),
});
/**
 * P33 — Send a chat message. Server validates: channel access (global is
 * always open; `faction:X` requires `startingFaction === X`), body
 * length (≤ 280), and persistence + broadcast.
 *
 *   - On success: server replies with `commandResult.ok = true` AND
 *     fans the message out to every subscribed session via a
 *     `chatMessage` broadcast (sender included).
 *   - Errors: `INVALID_CHANNEL`, `CHANNEL_ACCESS_DENIED`, `BODY_TOO_LONG`.
 */
export const ChatSendMessage = z.object({
    type: z.literal('chatSend'),
    requestId: z.string().min(1),
    // Channel grammar today (P34):
    //   `global`               — 6 chars
    //   `faction:<id>`         — ≤ 40 chars
    //   `dm:<uuidA>:<uuidB>`   — 76 chars (3 + 36 + 1 + 36)
    // 80 leaves a few chars of headroom for future channel kinds.
    channel: z.string().min(1).max(80),
    body: z.string().min(1).max(280),
});
// ── P36 — Player-to-player trade ─────────────────────────────────────────
/** A single bundle of resources + credits, used on both sides of a trade. */
export const TradeBundleSchema = z.object({
    resources: z
        .array(z.object({
        resourceId: z.string().min(1),
        qty: z.number().int().positive(),
    }))
        .max(8),
    credits: z.number().int().nonnegative(),
});
/** Wire-shape for a trade proposal (matches the DB row, sans userId raw). */
export const TradeProposalStatusSchema = z.union([
    z.literal('pending'),
    z.literal('accepted'),
    z.literal('rejected'),
    z.literal('expired'),
    z.literal('cancelled'),
]);
export const TradeProposalDtoSchema = z.object({
    id: z.string(),
    status: TradeProposalStatusSchema,
    /** Sender. */
    from: z.object({
        userId: z.string(),
        corpName: z.string(),
        corpSlug: z.string(),
        emblem: EmblemSchema.nullable(),
    }),
    /** Recipient. */
    to: z.object({
        userId: z.string(),
        corpName: z.string(),
        corpSlug: z.string(),
        emblem: EmblemSchema.nullable(),
    }),
    offer: TradeBundleSchema,
    ask: TradeBundleSchema,
    note: z.string().nullable(),
    createdAt: z.string(),
    expiresAt: z.string(),
    settledAt: z.string().nullable(),
});
/**
 * Server → client live update. Fires when a trade involving the recipient
 * changes status (proposed, accepted, rejected, cancelled, expired). The
 * payload is the full DTO so clients can update their inbox without a
 * round-trip.
 */
export const TradeProposalUpdate = z.object({
    type: z.literal('tradeProposalUpdate'),
    proposal: TradeProposalDtoSchema,
});
// ── P37 — Recurring player contracts ─────────────────────────────────────
export const RecurringTradeStatusSchema = z.union([
    z.literal('pending'),
    z.literal('active'),
    z.literal('completed'),
    z.literal('cancelled'),
    z.literal('expired'),
]);
export const RecurringTradeDtoSchema = z.object({
    id: z.string(),
    status: RecurringTradeStatusSchema,
    from: z.object({
        userId: z.string(),
        corpName: z.string(),
        corpSlug: z.string(),
        emblem: EmblemSchema.nullable(),
    }),
    to: z.object({
        userId: z.string(),
        corpName: z.string(),
        corpSlug: z.string(),
        emblem: EmblemSchema.nullable(),
    }),
    offer: TradeBundleSchema,
    ask: TradeBundleSchema,
    note: z.string().nullable(),
    cycleSeconds: z.number().int().positive(),
    cyclesTotal: z.number().int().positive(),
    cyclesRemaining: z.number().int().nonnegative(),
    nextCycleAt: z.string(),
    lastSettledAt: z.string().nullable(),
    createdAt: z.string(),
    expiresAt: z.string(),
});
/** Server → client broadcast when a recurring contract changes status,
 *  settles a cycle, or completes. Pushes both participants. */
export const RecurringTradeUpdate = z.object({
    type: z.literal('recurringTradeUpdate'),
    recurring: RecurringTradeDtoSchema,
});
// ── G1 — Megaprojects ────────────────────────────────────────────────────
export const MegaprojectStatusSchema = z.union([
    z.literal('active'),
    z.literal('completed'),
    z.literal('expired'),
    z.literal('cancelled'),
]);
export const MegaprojectDtoSchema = z.object({
    id: z.string(),
    factionId: z.string(),
    name: z.string(),
    description: z.string(),
    goalResourceId: z.string(),
    goalQuantity: z.number().int().positive(),
    progress: z.number().int().nonnegative(),
    status: MegaprojectStatusSchema,
    rewardKind: z.literal('faction_bonus'),
    rewardPayload: z.object({
        multiplier: z.number().positive(),
        durationTicks: z.number().int().positive(),
    }),
    createdAt: z.string(),
    expiresAt: z.string(),
    completedAt: z.string().nullable(),
    /** Top-N contributors aggregated server-side for the leaderboard. */
    topContributors: z
        .array(z.object({
        userId: z.string(),
        corpName: z.string(),
        corpSlug: z.string(),
        emblem: EmblemSchema.nullable(),
        contributed: z.number().int().nonnegative(),
    }))
        .optional(),
});
export const MegaprojectUpdate = z.object({
    type: z.literal('megaprojectUpdate'),
    megaproject: MegaprojectDtoSchema,
});
// ── F1 — Achievement unlock ──────────────────────────────────────────────
export const AchievementUnlock = z.object({
    type: z.literal('achievementUnlock'),
    achievementId: z.string(),
    unlockedAt: z.string(),
});
// ── P35 follow-up — "X just followed you" notification ───────────────────
export const FollowNotification = z.object({
    type: z.literal('followNotification'),
    follower: z.object({
        userId: z.string(),
        corpName: z.string(),
        corpSlug: z.string(),
        emblem: EmblemSchema.nullable(),
    }),
});
/**
 * P7 — buy a research node. The server validates against the player's
 * `researchPoints`, `purchasedResearch`, and the node's `prereqs`. On success
 * RP is deducted and the id is appended to `purchasedResearch`; the next
 * snapshot reflects the unlock.
 */
export const PurchaseResearchMessage = z.object({
    type: z.literal('purchaseResearch'),
    requestId: z.string().min(1),
    nodeId: z.string().min(1),
});
/**
 * P13 — Spin off the active station as a passive-income subsidiary.
 *
 * The server validates eligibility (milestone-tier-3 owned, lifetime
 * credits >= threshold, station has machines, subsidiary cap not hit) and
 * applies the chosen perk permanently. The active station resets — fresh
 * machines/drones, starter credit balance — but research + lifetime stats
 * are preserved (the whole point of "soft prestige"). The picked perk's
 * id must come from `ALL_SUBSIDIARY_PERK_IDS`.
 */
export const SpinOffSubsidiaryMessage = z.object({
    type: z.literal('spinOffSubsidiary'),
    requestId: z.string().min(1),
    perkId: z.string().min(1),
    /** Optional player-supplied display name; clamped to 40 chars server-side. */
    name: z.string().max(80).optional(),
});
// ── P16b — Conveyor belt commands ─────────────────────────────────────────
//
// Belts are placed one tile at a time (drag-to-route is v2 polish) with
// an orientation (the direction items flow on this tile) and a tier (1 or
// 2 in v1; T3+ deferred). The server validates tile-occupancy + funds and
// returns the new belt id on success.
export const PlaceBeltMessage = z.object({
    type: z.literal('placeBelt'),
    requestId: z.string().min(1),
    position: z.object({
        x: z.number().int().nonnegative(),
        y: z.number().int().nonnegative(),
    }),
    orientation: z.enum(['N', 'E', 'S', 'W']),
    tier: z.union([z.literal(1), z.literal(2)]),
    /**
     * P16c — vertical layer. `0` (default) is the ground belt; `1` is an
     * elevated overpass that floats above the ground level so two belt
     * routes can cross without interfering. Optional for back-compat with
     * pre-16c clients (server treats absent as 0).
     */
    level: z.union([z.literal(0), z.literal(1)]).optional(),
});
export const RemoveBeltMessage = z.object({
    type: z.literal('removeBelt'),
    requestId: z.string().min(1),
    beltId: z.string().min(1),
});
// ── P16b v1.1 — Belt junctions (splitters + mergers) ────────────────────────
//
// Junctions branch (splitter, 1→2) or merge (merger, 2→1) belt flows.
// Same wire shape as `PlaceBelt` plus a `kind` discriminator. Orientation
// convention is documented on `BeltJunction` in `belts.ts`.
export const PlaceJunctionMessage = z.object({
    type: z.literal('placeJunction'),
    requestId: z.string().min(1),
    position: z.object({
        x: z.number().int().nonnegative(),
        y: z.number().int().nonnegative(),
    }),
    kind: z.enum(['splitter', 'merger']),
    orientation: z.enum(['N', 'E', 'S', 'W']),
    tier: z.union([z.literal(1), z.literal(2)]),
    /**
     * P16c — vertical layer (level-bound junction; matches the level of the
     * belts it joins). Optional for pre-16c clients; absent = `0`.
     */
    level: z.union([z.literal(0), z.literal(1)]).optional(),
});
export const RemoveJunctionMessage = z.object({
    type: z.literal('removeJunction'),
    requestId: z.string().min(1),
    junctionId: z.string().min(1),
});
/**
 * P16b v1.2 — set a junction's routing config. Either or both fields can be
 * supplied; an omitted field leaves the existing value unchanged. Filter
 * entries with side `null` are removed (resets to "either" / fall-through).
 */
export const ConfigureJunctionMessage = z.object({
    type: z.literal('configureJunction'),
    requestId: z.string().min(1),
    junctionId: z.string().min(1),
    priorityMode: z.enum(['roundRobin', 'leftFirst', 'rightFirst']).optional(),
    /** Per-resource filter updates. Side `null` clears the entry. */
    filters: z.record(z.string().min(1), z.union([z.enum(['left', 'right']), z.null()])).optional(),
});
// ── P12 — AI Charter admin commands ───────────────────────────────────────
//
// All gated by `adminToken` (same env-driven pattern as setTuning). The
// server returns the updated bot list via `commandResult.payload` (loose
// JSON since it's admin-only and can drift independently of the typed wire).
export const AIListMessage = z.object({
    type: z.literal('aiList'),
    requestId: z.string().min(1),
    adminToken: z.string().optional(),
});
export const AISpawnMessage = z.object({
    type: z.literal('aiSpawn'),
    requestId: z.string().min(1),
    adminToken: z.string().optional(),
    /** "topUp" forces population to target; "many" spawns N random; "one"
     *  spawns a single bot with explicit archetype + faction. */
    mode: z.enum(['topUp', 'many', 'one']),
    count: z.number().int().min(1).max(50).optional(),
    archetype: z.string().optional(),
    faction: z.string().optional(),
    name: z.string().max(80).optional(),
});
export const AIRetireMessage = z.object({
    type: z.literal('aiRetire'),
    requestId: z.string().min(1),
    adminToken: z.string().optional(),
    botId: z.string().min(1),
    /** When `true`, hard-delete instead of the standard "retired but listed"
     *  state. Used for emergency cleanup; default behaviour is the soft path. */
    force: z.boolean().optional(),
});
export const AISetRatesMessage = z.object({
    type: z.literal('aiSetRates'),
    requestId: z.string().min(1),
    adminToken: z.string().optional(),
    botId: z.string().min(1),
    creditsPerMin: z.number().nonnegative().optional(),
    rpPerMin: z.number().nonnegative().optional(),
});
/**
 * P28.1 — Admin: seed extra deposits onto a station's mining zone.
 *
 * Closes the "fresh charter has only iron deposits" gap for charters that
 * pre-date P26's per-hub profiles. Picks `quantity` random unoccupied
 * mining-zone tiles and drops `resourceId` deposits on them at the
 * profile's normal speed-bonus distribution. Does NOT remove existing
 * deposits, machines, or belts — additive only.
 *
 * Gated on the same `adminToken` env-var pattern as setTuning/AI commands.
 * `stationId` defaults to the active station when omitted.
 *
 * Caps: `quantity` is clamped to `[1, 12]` server-side to keep the map
 * legible. `INSUFFICIENT_TILES` if the mining zone is too crowded.
 */
export const AdminSeedDepositsMessage = z.object({
    type: z.literal('adminSeedDeposits'),
    requestId: z.string().min(1),
    adminToken: z.string().optional(),
    stationId: z.string().optional(),
    resourceId: z.string().min(1),
    quantity: z.number().int().min(1).max(12),
});
/**
 * P17B — unlock the next station slot. The server validates that the
 * player's credits >= STATION_UNLOCK_THRESHOLDS[currentSlotCount] and
 * that the charter hasn't hit MAX_STATIONS_PER_CHARTER. On success
 * credits are deducted, a fresh procedural station is created, and the
 * next snapshot includes it in `stations[]`.
 *
 *   - `ok: true, createdId: <stationId>` → unlock applied.
 *   - `ok: false, code: 'INSUFFICIENT_CREDITS'`
 *   - `ok: false, code: 'MAX_STATIONS'`
 */
export const UnlockNextStationMessage = z.object({
    type: z.literal('unlockNextStation'),
    requestId: z.string().min(1),
    /** Optional display name (clamped to 40 chars server-side). */
    name: z.string().max(40).optional(),
});
/**
 * P10 — Defect to a different starting faction. Wipes reputation back
 * to the new faction's signup baseline (+10 with new, −2 with new's
 * rival, 0 with everyone else), updates `Player.startingFaction`, and
 * deducts the credits cost (`DEFECTION_CREDIT_COST`).
 *
 *   - `ok: true`                              → defection complete.
 *   - `ok: false, code: 'INSUFFICIENT_CREDITS'`
 *   - `ok: false, code: 'SAME_FACTION'`        → already with this one.
 *   - `ok: false, code: 'UNKNOWN_FACTION'`
 */
export const DefectToFactionMessage = z.object({
    type: z.literal('defectToFaction'),
    requestId: z.string().min(1),
    factionId: z.string().min(1),
});
/**
 * P28 — Buy a resource at the Trade Port for credits.
 *
 * Pays `quantity × marketPrice × BUY_MARKUP` and drops the bought
 * resource into the addressed Trade Port's input buffer (cap respected
 * — the call fails if the buffer can't hold it). Markup applies so
 * round-tripping (buy then sell at the same Trade Port) costs credits;
 * it's an escape hatch for "I have no copper deposits on this hub" not
 * a free arbitrage.
 *
 *   - `ok: true`                                    → bought.
 *   - `ok: false, code: 'INSUFFICIENT_CREDITS'`
 *   - `ok: false, code: 'BUFFER_FULL'`               → not enough room.
 *   - `ok: false, code: 'MACHINE_NOT_FOUND'`         → unknown id.
 *   - `ok: false, code: 'NOT_TRADE_PORT'`            → wrong machine kind.
 *   - `ok: false, code: 'UNKNOWN_RESOURCE'`
 */
export const BuyResourceMessage = z.object({
    type: z.literal('buyResource'),
    requestId: z.string().min(1),
    /** The Trade Port machine to deposit into. */
    machineId: z.string().min(1),
    /** Resource id (`'iron-ore'`, `'copper-ore'`, etc.). */
    resourceId: z.string().min(1),
    /** Units to buy. Must be positive. */
    quantity: z.number().int().positive(),
});
/**
 * P17B — switch which station the player's snapshot focuses on. Pure
 * UI-state command: the server tracks the active station so multiple
 * tabs/devices stay in sync, but no sim state changes. The next
 * snapshot reflects the new active station's `station`, `power`, and
 * (eventually) per-station inspector views.
 *
 *   - `ok: true`                 → switched.
 *   - `ok: false, code: 'STATION_NOT_OWNED'`
 */
export const SetActiveStationMessage = z.object({
    type: z.literal('setActiveStation'),
    requestId: z.string().min(1),
    stationId: z.string().min(1),
});
export const ClientMessage = z.discriminatedUnion('type', [
    HelloMessage,
    PingMessage,
    PlaceMachineMessage,
    RemoveMachineMessage,
    PlaceDroneMessage,
    RemoveDroneMessage,
    ConfigureDroneRuleMessage,
    SetMachineRecipeMessage,
    SetTuningMessage,
    ClearTuningMessage,
    AcceptContractMessage,
    AbandonContractMessage,
    ResetActiveContractsMessage,
    PurchaseResearchMessage,
    SpinOffSubsidiaryMessage,
    AIListMessage,
    AISpawnMessage,
    AIRetireMessage,
    AISetRatesMessage,
    PlaceBeltMessage,
    RemoveBeltMessage,
    PlaceJunctionMessage,
    RemoveJunctionMessage,
    ConfigureJunctionMessage,
    UnlockNextStationMessage,
    SetActiveStationMessage,
    DefectToFactionMessage,
    AcceptSharedContractMessage,
    BidSharedContractMessage,
    BuyResourceMessage,
    AdminSeedDepositsMessage,
    ChatSendMessage,
]);
// ---------- Server → Client ----------
export const WelcomeMessage = z.object({
    type: z.literal('welcome'),
    sessionId: z.string().uuid(),
    serverTs: z.number().int().nonnegative(),
    protocol: z.literal(PROTOCOL_VERSION),
    /** Current sim tick the server is on, for client-side time reconciliation. */
    tick: z.number().int().nonnegative(),
    /** Authenticated user this WS session belongs to (added in P3A). */
    user: PublicUserSchema,
});
/**
 * Sent once on session connect when the server fast-forwarded the world while
 * the user was offline. Drives the "While you were away…" modal. `elapsedMs`
 * may be capped (see OFFLINE_CAP_TICKS) so don't show it as wall-clock truth.
 */
export const OfflineSummaryMessage = z.object({
    type: z.literal('offlineSummary'),
    /** Wall-clock ms between last save and current load, post-cap. */
    elapsedMs: z.number().int().nonnegative(),
    /** Sim ticks fast-forwarded (already capped). */
    ticksApplied: z.number().int().nonnegative(),
    creditsEarned: z.number().nonnegative(),
    /** Per-resource units sold during the offline window. */
    soldByResource: z.record(z.string(), z.number().nonnegative()),
    /** True if the elapsed window was clipped to the cap. */
    capped: z.boolean(),
});
export const PongMessage = z.object({
    type: z.literal('pong'),
    nonce: z.string(),
    /** Echoed client timestamp — lets the client compute RTT without a clock. */
    clientTs: z.number().int().nonnegative(),
    serverTs: z.number().int().nonnegative(),
});
export const ErrorMessage = z.object({
    type: z.literal('error'),
    /** Stable machine-readable error code (e.g. "PROTOCOL_MISMATCH"). */
    code: z.string().min(1),
    /** Human-readable message, safe to show to the user. */
    message: z.string(),
});
/**
 * Full world-state snapshot. Phase 1 broadcasts one of these per tick.
 * Later phases (post-P7) may replace this with incremental diffs for bandwidth.
 *
 *  - P2 added `player` (credits, reputation, RP).
 *  - P2A added `tuning` — the live admin override list so every client sees
 *    up-to-the-tick tunable values without a separate channel.
 */
export const WorldSnapshotMessage = z.object({
    type: z.literal('worldSnapshot'),
    tick: z.number().int().nonnegative(),
    /**
     * Legacy single-station field — kept for back-compat with pre-P17B
     * clients. Always equals `stations[i]` where `stations[i].id ===
     * activeStationId`. New code should prefer `stations` + `activeStationId`.
     */
    station: StationSchema,
    player: PlayerSchema,
    tuning: z.array(TuningOverrideSchema),
    /** P4 — rolling pool of open offers for the player to pick from. */
    contractOffers: z.array(ContractOfferSchema),
    /** P4 — accepted contracts still in flight (not yet completed/expired). */
    activeContracts: z.array(ActiveContractSchema),
    /** P4A — live prices, baseline, history, session high/low. */
    market: MarketStateSchema,
    /** P5 — per-world kW budget: generation, demand, capacitor, throttle ratio.
     *  Reflects the active station only. Inactive stations have their own
     *  power grids; switch the active station to inspect theirs. */
    power: PowerBudgetSchema,
    /** P8 — currently-active reactive events. Empty array when nothing is going on. */
    events: z.array(ActiveEventSchema),
    /** P11 — Resonant Matrix bottleneck advisor. Always present; `unlocked: false`
     *  when the player hasn't reached milestone-tier-5, in which case the entries
     *  list is empty and the client hides the panel. */
    bottlenecks: BottleneckReportSchema,
    /**
     * P17B — every station the charter currently owns, in unlock order.
     * `stations[0]` is always Hub Alpha (the starter). Length grows by 1
     * each time the player calls `unlockNextStation` and pays the gate.
     * Optional for legacy-client compat: pre-P17B clients ignore this and
     * read `station` instead.
     */
    stations: z.array(StationSchema).optional(),
    /**
     * P17B — id of the currently-active station. The legacy `station`
     * field above is always equal to the entry in `stations` matching
     * this id. Default-rendered station on the client.
     */
    activeStationId: z.string().optional(),
    /**
     * P17C — Cross-Station Stockpile. Charter-wide shared inventory tied
     * to every `cross-station-port` machine across every station the
     * player owns. `capacity` is the sum of CSP contributions; `contents`
     * tracks current quantities per resource. Optional for legacy
     * client compat — pre-P17C clients ignore the field.
     */
    crossStationStockpile: z
        .object({
        capacity: z.number().nonnegative(),
        contents: z.record(z.string(), z.number().nonnegative()),
    })
        .optional(),
    /**
     * P22 — Shared Bid Board. Process-global pool of premium contracts
     * that every connected human + every AI charter can race to claim.
     * Optional for legacy-client compat: pre-P22 clients ignore the
     * field. The same snapshot is broadcast to all connected sessions
     * (the pool is shared, not per-charter).
     */
    bidBoard: BidBoardSnapshotSchema.optional(),
});
/**
 * Ack for a build command. `ok: true` means the world mutated; the next
 * snapshot will reflect the change. `createdId` is populated for commands
 * that create a new entity (placeMachine, placeDrone).
 */
export const CommandResultMessage = z.object({
    type: z.literal('commandResult'),
    requestId: z.string().min(1),
    ok: z.boolean(),
    createdId: z.string().optional(),
    code: z.string().optional(),
    message: z.string().optional(),
    /** P12 — admin commands (aiList / aiSpawn / aiRetire / aiSetRates) attach
     *  arbitrary JSON here. Loose check; the admin client knows what it asked
     *  for. Real-player commands ignore this field. */
    payload: z.unknown().optional(),
});
/**
 * P33 — Chat broadcast. Sent to every session subscribed to the
 * channel (global → all sessions; faction:X → only members of X).
 * The author's emblem tone is denormalised so the renderer doesn't
 * need a per-message `/u/:slug` round-trip.
 */
export const ChatAuthorRefSchema = z.object({
    kind: z.union([z.literal('human'), z.literal('ai')]),
    corpName: z.string(),
    /** Resolved palette ref ('amber' | 'green' | …); `null` for AI bots. */
    emblemTone: z.string().nullable().optional(),
});
export const ChatMessageBroadcast = z.object({
    type: z.literal('chatMessage'),
    id: z.string(),
    channel: z.string(),
    author: ChatAuthorRefSchema,
    body: z.string(),
    sentAt: z.string(), // ISO-8601
});
export const ServerMessage = z.discriminatedUnion('type', [
    WelcomeMessage,
    PongMessage,
    ErrorMessage,
    WorldSnapshotMessage,
    CommandResultMessage,
    OfflineSummaryMessage,
    ChatMessageBroadcast,
    TradeProposalUpdate,
    RecurringTradeUpdate,
    FollowNotification,
    AchievementUnlock,
    MegaprojectUpdate,
]);
// ---------- Helpers ----------
/** Parse a raw JSON string as a ClientMessage. Returns null on failure. */
export function parseClientMessage(raw) {
    try {
        const parsed = ClientMessage.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : null;
    }
    catch {
        return null;
    }
}
/** Parse a raw JSON string as a ServerMessage. Returns null on failure. */
export function parseServerMessage(raw) {
    try {
        const parsed = ServerMessage.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=messages.js.map